import {
  Box,
  Button,
  ButtonProps,
  Modal,
  Paper,
  Skeleton,
  Stack,
  SxProps,
  Theme,
} from '@mui/material';
import { useState } from 'react';
import { StyledTheme } from '../../../styles/styleTheme';
import { buttonDefaultStyles, defaultStyles } from './style';

type Props = {
  children: React.ReactChild;
  buttonProps?: ButtonProps & { buttonText: string };
  modalProps?: {
    style: SxProps<Theme>;
  };
  showChildrenDefault?: boolean;
  actionButtons?: {
    sx?: SxProps<Theme>;
    cancelButtonProps?: ButtonProps & { buttonText: string };
    continueButtonProps?: ButtonProps & { buttonText: string };
  };

  onCancel?: () => void;
  onContinue?: () => void;
};

export const CustomModal = ({
  children,
  buttonProps,
  modalProps,
  showChildrenDefault = true,
  actionButtons,
  onCancel,
  onContinue,
}: Props) => {
  const [open, setOpen] = useState(false);

  const handleToggle = () => setOpen((prev) => !prev);

  const mergeSx = (base: SxProps<Theme>, override?: SxProps<Theme>): SxProps<Theme> => {
    if (!override) {
      return base;
    }

    if (Array.isArray(base) || Array.isArray(override)) {
      return ([] as SxProps<Theme>[]).concat(base, override) as SxProps<Theme>;
    }

    return { ...base, ...override };
  };

  return (
    <>
      <Button
        className='custom-modal-button'
        {...buttonProps}
        sx={{
          ...({
            '&.custom-modal-button': {
              color: StyledTheme.white,
              backgroundColor: StyledTheme.grayDark,
              ...(buttonProps?.sx || {}),
            },
          } as unknown as SxProps<Theme>),
        }}
        onClick={handleToggle}>
        {buttonProps?.buttonText ?? 'Replace me'}
      </Button>

      <Modal
        open={open}
        onClose={handleToggle}
        BackdropProps={{
          timeout: 500,
        }}>
        <Stack sx={mergeSx(defaultStyles, modalProps?.style)} flexDirection='column' spacing={4}>
          <Box
            height='100%'
            display='flex'
            flexDirection='column'
            justifyContent='space-around'
            width='100%'
            alignItems='center'>
            {showChildrenDefault ? (
              <Paper sx={{ width: '100%', height: '91%' }}>
                {children ?? (
                  <Skeleton
                    animation='wave'
                    variant='rectangular'
                    width='100%'
                    height='100%'
                    sx={{ borderRadius: '5px', backgroundColor: 'rgba(0, 0, 0, 0.12)' }}
                  />
                )}
              </Paper>
            ) : (
              children
            )}

            <Box
              display='flex'
              width='100%'
              gap={2}
              sx={{
                ...(actionButtons?.sx || { justifyContent: 'flex-end' }),
              }}>
              {actionButtons?.cancelButtonProps && (
                <Button
                  onClick={() => {
                    handleToggle();
                    onCancel?.();
                  }}
                  sx={mergeSx(buttonDefaultStyles, {
                    ...actionButtons.cancelButtonProps.sx,
                    display: 'flex',
                  })}>
                  {actionButtons.cancelButtonProps.buttonText || 'Close'}
                </Button>
              )}
              {actionButtons?.continueButtonProps && (
                <Button
                  onClick={() => {
                    handleToggle();
                    onContinue?.();
                  }}
                  {...actionButtons.continueButtonProps}
                  sx={mergeSx(buttonDefaultStyles, {
                    ...actionButtons.continueButtonProps.sx,
                    display: 'flex',
                  })}>
                  {actionButtons.continueButtonProps.buttonText || 'Continue'}
                </Button>
              )}
            </Box>
          </Box>
        </Stack>
      </Modal>
    </>
  );
};
